'use client'
import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

export interface DialogProps {
  open: boolean
  onClose?: () => void
  children: React.ReactNode
  fullScreen?: boolean
  fullWidth?: boolean
  maxWidth?: string | false
  className?: string
  PaperProps?: {
    sx?: {
      width?: string | number
      height?: string | number
      backgroundColor?: string
      [key: string]: unknown
    }
    className?: string
  }
  'data-testid'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  children,
  fullScreen = false,
  fullWidth = false,
  maxWidth,
  className = '',
  PaperProps = {},
  'data-testid': testId,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)

  useBodyScrollLock(open)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        onClose()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && e.target === dialogRef.current && onClose) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('click', handleClickOutside)
    }
  }, [open, onClose])

  if (!open) return null

  const getWidthClasses = () => {
    if (fullScreen) return 'w-full h-full'

    if (maxWidth === false) {
      return 'w-auto'
    }

    if (PaperProps.sx?.width) {
      return 'w-auto'
    }

    if (fullWidth) {
      return 'w-full'
    }

    return 'w-full max-w-lg'
  }

  const getHeightClasses = () => {
    if (fullScreen) return 'h-full'
    return 'h-auto max-h-screen'
  }

  const getBackgroundClasses = () => {
    if (PaperProps.sx?.backgroundColor === 'transparent') {
      return ''
    }
    return 'bg-white'
  }

  const getPaddingClasses = () => {
    if (PaperProps.sx?.backgroundColor === 'transparent') {
      return ''
    }
    return 'p-6'
  }

  const getBorderRadius = () => {
    if (PaperProps.sx?.backgroundColor === 'transparent') {
      return ''
    }
    return 'rounded-lg'
  }

  const getShadow = () => {
    if (PaperProps.sx?.backgroundColor === 'transparent') {
      return ''
    }
    return 'shadow-xl'
  }

  const paperClasses = `
    ${getWidthClasses()}
    ${getHeightClasses()}
    ${getBackgroundClasses()}
    ${getPaddingClasses()}
    ${getBorderRadius()}
    ${getShadow()}
    ${PaperProps.className || ''}
  `.trim()

  const paperStyle = PaperProps.sx
    ? {
        width: PaperProps.sx.width,
        height: PaperProps.sx.height,
        backgroundColor: PaperProps.sx.backgroundColor,
      }
    : {}

  const getContainerPadding = () => {
    if (PaperProps.sx?.backgroundColor === 'transparent') {
      return ''
    }
    return 'p-4'
  }

  const dialogContent = (
    <div
      ref={dialogRef}
      className={`fixed inset-0 z-50 flex items-center justify-center ${getContainerPadding()} ${className}`}
      data-testid={testId}
    >
      {!className && <div className="absolute inset-0 bg-black/50" />}

      <div
        className={paperClasses}
        style={paperStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
      >
        {children}
      </div>
    </div>
  )
  return createPortal(dialogContent, document.body)
}
